import { Table as TableInstance, flexRender } from '@tanstack/react-table';

import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface GenericTableProps<TData> {
  table: TableInstance<TData>;
  loading?: boolean;
  skeletonRows?: number;
  onClick?: (row: TData) => void;
  emptyMessage?: string;
  containerClassName?: string;
}

export function GenericTable<TData>({
  table,
  loading,
  onClick,
  skeletonRows = 5,
  emptyMessage = 'No data found.',
  containerClassName,
}: GenericTableProps<TData>) {
  return (
    <div className={containerClassName}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{ width: header.column.columnDef.size }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableSkeleton table={table} skeletonRows={skeletonRows} />
          ) : table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className={cn(onClick ? 'cursor-pointer hover:bg-muted' : '')}
                onClick={onClick ? () => onClick(row.original) : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{ width: cell.column.columnDef.size }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

interface TableSkeletonProps<TData> {
  table: TableInstance<TData>;
  skeletonRows: number;
}

function TableSkeleton<TData>({
  table,
  skeletonRows,
}: TableSkeletonProps<TData>) {
  return (
    <>
      {Array(skeletonRows)
        .fill(0)
        .map((_, index) => (
          <TableRow key={index}>
            {table.getAllColumns().map((column) => {
              const size = column.columnDef.size ?? 100;
              return (
                <TableCell key={column.id}>
                  {column.columnDef.id === 'actions' ? (
                    <div className="flex gap-2 justify-end">
                      <Skeleton className="h-8 w-8" />
                      <Skeleton className="h-8 w-8" />
                    </div>
                  ) : (
                    <Skeleton
                      className={`h-6 w-[${Math.max(size - 40, 60)}px]`}
                    />
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
    </>
  );
}
